import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { PagesService } from '../../../shared/services/pages.service';
import { MediaService, type MediaAssetDto } from '../../../shared/services/media.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminSkeletonComponent,
  AdminTableComponent,
} from '../../../shared/admin';

export type SuccessStoryFormCard = {
  key: string;
  pill: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
  mediaAssetId: string;
};

type SuccessStoriesForm = {
  successStoriesTitle: string;
  successStoriesSubtitle: string;
  successStories: SuccessStoryFormCard[];
};

const emptyStoryCard = (key: string): SuccessStoryFormCard => ({
  key,
  pill: 'Featured Story',
  title: '',
  description: '',
  ctaLabel: 'Read the Full Story',
  ctaHref: '',
  mediaAssetId: '',
});

const TABLE_PAGE_SIZE = 10;

@Component({
  selector: 'app-success-stories-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminCardComponent,
    AdminSkeletonComponent,
    AdminTableComponent,
    AdminModalComponent,
    AdminEmptyStateComponent,
  ],
  templateUrl: './success-stories-page.component.html',
})
export class SuccessStoriesPageComponent {
  private readonly pages = inject(PagesService);
  private readonly media = inject(MediaService);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.successStories', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.successStories', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.successStories', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.successStories', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('website.successStories', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.successStories', 'delete'));
  readonly showUpload = computed(() => this.permissions.canShow('website.successStories', 'upload'));
  readonly canUpload = computed(() => this.permissions.canExecute('website.successStories', 'upload'));
  readonly canSaveModal = computed(() =>
    this.editingKey() ? this.canEdit() : this.canCreate(),
  );
  readonly showSaveModal = computed(() =>
    this.editingKey() ? this.showEdit() : this.showCreate(),
  );

  readonly tablePageSize = TABLE_PAGE_SIZE;
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly successStoryPreviewUrls = signal<Record<string, string>>({});
  readonly successStoryUploadingKey = signal<string | null>(null);
  readonly successStoryMessage = signal<string | null>(null);
  readonly showModal = signal(false);
  readonly editingKey = signal<string | null>(null);
  readonly modalSaving = signal(false);
  readonly modalError = signal<string | null>(null);
  readonly tablePage = signal(0);

  private successStoryFiles: Record<string, File | null> = {};
  modalDraft: SuccessStoryFormCard = emptyStoryCard('');

  form: SuccessStoriesForm = this.emptyForm();

  get totalStories(): number {
    return this.form.successStories.length;
  }

  get publishedCount(): number {
    return this.form.successStories.filter((story) => this.isPublished(story)).length;
  }

  get paginatedStories(): SuccessStoryFormCard[] {
    const start = this.tablePage() * TABLE_PAGE_SIZE;
    return this.form.successStories.slice(start, start + TABLE_PAGE_SIZE);
  }

  get paginationLabel(): string {
    const total = this.totalStories;
    if (total === 0) return 'Showing 0 of 0 success stories';
    const start = this.tablePage() * TABLE_PAGE_SIZE + 1;
    const end = Math.min((this.tablePage() + 1) * TABLE_PAGE_SIZE, total);
    return `Showing ${start}–${end} of ${total} success stories`;
  }

  get canPrevPage(): boolean {
    return this.tablePage() > 0;
  }

  get canNextPage(): boolean {
    return (this.tablePage() + 1) * TABLE_PAGE_SIZE < this.totalStories;
  }

  constructor() {
    this.load();
  }

  emptyForm(): SuccessStoriesForm {
    return {
      successStoriesTitle: 'Global Success Stories',
      successStoriesSubtitle: 'Real journeys, life-changing achievements, and global impact.',
      successStories: [],
    };
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.pages.getPlatformMarketingHome().subscribe({
      next: (d) => {
        this.applyFromDoc(d);
        this.loadSuccessStoryPreviews();
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set('Could not load success stories.');
        this.form = this.emptyForm();
      },
    });
  }

  save(): void {
    this.saving.set(true);
    this.error.set(null);
    this.pages.patchPlatformMarketingHome(this.buildPayload()).subscribe({
      next: (d) => {
        this.applyFromDoc(d);
        this.loadSuccessStoryPreviews();
        this.saving.set(false);
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Save failed.');
      },
    });
  }

  newSuccessStoryKey(): string {
    return `story-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  openCreate(): void {
    const key = this.newSuccessStoryKey();
    this.modalDraft = emptyStoryCard(key);
    this.editingKey.set(null);
    this.modalError.set(null);
    this.successStoryMessage.set(null);
    delete this.successStoryFiles[key];
    this.showModal.set(true);
  }

  openEdit(story: SuccessStoryFormCard): void {
    this.modalDraft = { ...story };
    this.editingKey.set(story.key);
    this.modalError.set(null);
    this.successStoryMessage.set(null);
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.modalError.set(null);
    this.successStoryMessage.set(null);
    const key = this.modalDraft.key;
    if (!this.editingKey() && !this.form.successStories.some((s) => s.key === key)) {
      delete this.successStoryFiles[key];
    }
  }

  saveModal(): void {
    const title = this.modalDraft.title.trim();
    const description = this.modalDraft.description.trim();
    if (!title || !description) {
      this.modalError.set('Title and story summary are required.');
      return;
    }

    const card: SuccessStoryFormCard = {
      ...this.modalDraft,
      title,
      description,
      pill: this.modalDraft.pill.trim() || 'Featured Story',
      ctaLabel: this.modalDraft.ctaLabel.trim() || 'Read the Full Story',
      ctaHref: this.modalDraft.ctaHref.trim(),
      mediaAssetId: this.modalDraft.mediaAssetId.trim(),
    };

    this.modalSaving.set(true);
    this.modalError.set(null);

    const editing = this.editingKey();
    if (editing) {
      this.form.successStories = this.form.successStories.map((s) =>
        s.key === editing ? card : s,
      );
    } else {
      this.form.successStories = [...this.form.successStories, card];
    }

    this.persistPartial(
      () => {
        this.modalSaving.set(false);
        this.showModal.set(false);
      },
      (msg) => {
        this.modalSaving.set(false);
        this.modalError.set(msg);
      },
    );
  }

  removeSuccessStory(key: string): void {
    const story = this.form.successStories.find((s) => s.key === key);
    const label = story?.title.trim().split('\n')[0] || 'this story';
    if (!confirm(`Remove "${label}"?`)) return;

    this.form.successStories = this.form.successStories.filter((s) => s.key !== key);
    delete this.successStoryFiles[key];
    this.successStoryPreviewUrls.update((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    const maxPage = Math.max(0, Math.ceil(this.form.successStories.length / TABLE_PAGE_SIZE) - 1);
    if (this.tablePage() > maxPage) {
      this.tablePage.set(maxPage);
    }

    this.persistPartial(
      () => undefined,
      (msg) => this.error.set(msg),
    );
  }

  prevPage(): void {
    if (this.canPrevPage) this.tablePage.update((p) => p - 1);
  }

  nextPage(): void {
    if (this.canNextPage) this.tablePage.update((p) => p + 1);
  }

  successStoryPreview(key: string): string | null {
    return this.successStoryPreviewUrls()[key] ?? null;
  }

  hasSuccessStoryFile(key: string): boolean {
    return !!this.successStoryFiles[key];
  }

  onSuccessStoryFile(key: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.successStoryFiles[key] = input.files?.[0] ?? null;
    this.successStoryMessage.set(null);
  }

  uploadSuccessStoryImage(key: string): void {
    const file = this.successStoryFiles[key];
    if (!file) return;
    this.successStoryUploadingKey.set(key);
    this.successStoryMessage.set(null);
    this.media.upload(file, { altText: this.modalDraft.title || 'Success story' }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.successStoryUploadingKey.set(null);
          this.successStoryMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        this.modalDraft.mediaAssetId = assetId;
        const previewUrl = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
        this.successStoryPreviewUrls.update((prev) => ({ ...prev, [key]: previewUrl }));
        this.successStoryFiles[key] = null;
        this.successStoryUploadingKey.set(null);
        this.successStoryMessage.set('Image uploaded. Click Save story to apply.');
      },
      error: (err) => {
        this.successStoryUploadingKey.set(null);
        this.successStoryMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  storyTitlePreview(title: string): string {
    const line = title.trim().split('\n').find(Boolean);
    return line || 'Untitled story';
  }

  storySummaryPreview(description: string): string {
    const text = description.trim();
    if (text.length <= 140) return text;
    return `${text.slice(0, 137)}…`;
  }

  isPublished(story: SuccessStoryFormCard): boolean {
    return Boolean(
      story.title.trim() && story.description.trim() && story.mediaAssetId.trim(),
    );
  }

  private buildPayload(): Record<string, unknown> {
    const successStories = this.form.successStories
      .map((card) => {
        const title = card.title.trim();
        const description = card.description.trim();
        if (!title || !description) return null;
        const pill = card.pill.trim();
        const ctaLabel = card.ctaLabel.trim();
        const ctaHref = card.ctaHref.trim();
        const mediaAssetId = card.mediaAssetId.trim();
        return {
          title,
          description,
          ...(pill ? { pill } : {}),
          ...(ctaLabel ? { ctaLabel } : {}),
          ...(ctaHref ? { ctaHref } : {}),
          ...(mediaAssetId ? { mediaAssetId } : {}),
        };
      })
      .filter(Boolean);
    return {
      successStoriesTitle: this.form.successStoriesTitle || undefined,
      successStoriesSubtitle: this.form.successStoriesSubtitle || undefined,
      successStories,
    };
  }

  private persistPartial(onSuccess: () => void, onError: (message: string) => void): void {
    this.pages.patchPlatformMarketingHome(this.buildPayload()).subscribe({
      next: (d) => {
        this.applyFromDoc(d);
        this.loadSuccessStoryPreviews();
        onSuccess();
      },
      error: (err) => onError(err?.error?.message ?? 'Could not save success stories.'),
    });
  }

  private applyFromDoc(d: Record<string, unknown>): void {
    const entries = (d['successStories'] ?? []) as Array<{
      title?: string;
      description?: string;
      pill?: string;
      ctaLabel?: string;
      ctaHref?: string;
      mediaAssetId?: string;
    }>;
    const previousByKey = new Map(this.form.successStories.map((s) => [s.key, s]));
    this.form.successStoriesTitle = String(d['successStoriesTitle'] ?? this.form.successStoriesTitle);
    this.form.successStoriesSubtitle = String(d['successStoriesSubtitle'] ?? this.form.successStoriesSubtitle);
    this.form.successStories = entries.map((entry, index) => {
      const prev = [...previousByKey.values()][index];
      const key = prev?.key ?? this.newSuccessStoryKey();
      return {
        key,
        pill: String(entry.pill ?? prev?.pill ?? 'Featured Story'),
        title: String(entry.title ?? ''),
        description: String(entry.description ?? ''),
        ctaLabel: String(entry.ctaLabel ?? prev?.ctaLabel ?? 'Read the Full Story'),
        ctaHref: String(entry.ctaHref ?? prev?.ctaHref ?? ''),
        mediaAssetId: String(entry.mediaAssetId ?? prev?.mediaAssetId ?? ''),
      };
    });

    const maxPage = Math.max(0, Math.ceil(this.form.successStories.length / TABLE_PAGE_SIZE) - 1);
    if (this.tablePage() > maxPage) {
      this.tablePage.set(maxPage);
    }
  }

  private loadSuccessStoryPreviews(existing: Record<string, string> = {}): void {
    const previews: Record<string, string> = { ...existing };
    let pending = 0;
    for (const card of this.form.successStories) {
      const id = card.mediaAssetId?.trim();
      if (!id) continue;
      if (previews[card.key]) continue;
      pending += 1;
      this.media.getById(id).subscribe({
        next: (asset) => {
          previews[card.key] = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
          pending -= 1;
          if (pending === 0) this.successStoryPreviewUrls.set({ ...previews });
        },
        error: () => {
          pending -= 1;
          if (pending === 0) this.successStoryPreviewUrls.set({ ...previews });
        },
      });
    }
    if (pending === 0) this.successStoryPreviewUrls.set(previews);
  }

  private mediaAssetId(asset: MediaAssetDto): string {
    const raw = asset._id ?? (asset as MediaAssetDto & { id?: string }).id;
    return raw != null ? String(raw) : '';
  }
}
